import React, { useState } from 'react';
import { Modal } from 'antd';
import AccountManagement from '../components/AccountManagement';
import AccountConfigSummary from '../components/AccountConfigSummary';
import WhereUsed from '../components/WhereUsed';

/**
 * Centralizes the AccountManagement modal + Account info modal that should
 * be reachable from every table's right-click menu.
 *
 * Returns:
 *   - callbacks: spread these into every table that needs the shared menu items.
 *   - modals: render this once at the page root (e.g. inside <Layout>).
 *
 * The `handleX` callbacks for AccountManagement come from each page's existing
 * redux-bound action creators. They differ slightly per page (e.g. some pages
 * may not have custom-tab account deletion), so they're injected as args
 * rather than hardcoded.
 *
 * @param {Object} args
 * @param {Object} args.accountStructure
 * @param {Function} args.handleInsertAccount
 * @param {Function} args.handleCreateSubAccount
 * @param {Function} args.handleDeleteAccount
 * @param {Function} [args.handleDeleteCustomTabAccount]
 * @param {Function} [args.onLocateAccount]   - jumps to an account's row; links in "Where used" call it
 * @param {Object}   [args.scenario]          - open scenario, so "Where used" can list its cell formulas
 */
export function useAccountModals({
    accountStructure,
    handleInsertAccount,
    handleCreateSubAccount,
    handleDeleteAccount,
    handleDeleteCustomTabAccount,
    onLocateAccount,
    scenario,
}) {
    const [isAcctMgmtOpen, setIsAcctMgmtOpen] = useState(false);
    const [acctMgmtAccount, setAcctMgmtAccount] = useState(null);
    const [infoAccount, setInfoAccount] = useState(null);
    const [whereUsedAccount, setWhereUsedAccount] = useState(null);

    const callbacks = {
        onOpenAccountManagement: (account) => {
            if (account) setAcctMgmtAccount(account);
            setIsAcctMgmtOpen(true);
        },
        onShowAccountInfo: (account) => setInfoAccount(account),
        onShowWhereUsed: (account) => setWhereUsedAccount(account),
    };

    const modals = (
        <>
            <AccountManagement
                account={acctMgmtAccount}
                accountStructure={accountStructure}
                isAccountManagementOpen={isAcctMgmtOpen}
                handleInsertAccount={handleInsertAccount}
                handleCreateSubAccount={handleCreateSubAccount}
                handleDeleteAccount={handleDeleteAccount}
                handleDeleteCustomTabAccount={handleDeleteCustomTabAccount}
                handleClose={() => {
                    setIsAcctMgmtOpen(false);
                    setAcctMgmtAccount(null);
                }}
            />

            <Modal
                visible={infoAccount != null}
                onCancel={() => setInfoAccount(null)}
                footer={null}
                title="Account info"
                destroyOnClose
            >
                {infoAccount && <AccountConfigSummary account={infoAccount} />}
            </Modal>

            <Modal
                visible={whereUsedAccount != null}
                onCancel={() => setWhereUsedAccount(null)}
                footer={null}
                title="Where used"
                width={640}
                destroyOnClose
            >
                {whereUsedAccount && (
                    <WhereUsed
                        account={whereUsedAccount}
                        accountStructure={accountStructure}
                        scenario={scenario}
                        onLocate={
                            onLocateAccount
                                ? (account) => {
                                      setWhereUsedAccount(null);
                                      onLocateAccount(account);
                                  }
                                : undefined
                        }
                    />
                )}
            </Modal>
        </>
    );

    return { callbacks, modals };
}